import { type ReactNode } from "react";
import { Link } from "react-router";
import {
  ClockCircleOutlined,
  SettingOutlined,
  TeamOutlined,
} from "@ant-design/icons";

import BugIcon from "@/assets/icons/pest_control.svg?react";
import { useUser } from "@/contexts/UserContext";

export type AdminMenuItem = {
  key: string;
  label: ReactNode;
  icon: ReactNode;
  onClick?: () => void;
};

const BUG_REPORT_URL = "https://omniscience.fillout.com/bugreport";

function isRoutableAdminMenuItem(item: AdminMenuItem) {
  return item.key.startsWith("/");
}

const ADMIN_MENU_DEFINITIONS: AdminMenuItem[] = [
  {
    key: "/users-access",
    icon: <TeamOutlined />,
    label: <Link to="/admin/users-access">Users & Access</Link>,
  },
  {
    key: "/roles-permissions",
    icon: <SettingOutlined />,
    label: <Link to="/admin/roles-permissions">Roles & Permissions</Link>,
  },
  {
    key: "/activity-log",
    icon: <ClockCircleOutlined />,
    label: <Link to="/admin/activity-log">Activity Log</Link>,
  },
  {
    key: "report-a-bug",
    icon: <BugIcon className="w-[14px] h-[14px] scale-[1.4]" />,
    label: (
      <span className="!ms-0" data-testid="admin-menu-item-report-a-bug">
        Report a Bug
      </span>
    ),
    onClick: () => {
      window.open(BUG_REPORT_URL, "_blank", "noopener,noreferrer");
    },
  },
];

function matchesAdminMenuKey(pathname: string, key: string) {
  return pathname === key || pathname.startsWith(`${key}/`);
}

export function resolveAdminMenuKey(pathname: string) {
  const normalizedPathname = pathname.startsWith("/admin")
    ? pathname.replace(/^\/admin/, "") || "/"
    : pathname;

  const matchedItem = ADMIN_MENU_DEFINITIONS.find(
    (item) =>
      isRoutableAdminMenuItem(item) &&
      matchesAdminMenuKey(normalizedPathname, item.key),
  );

  return matchedItem?.key;
}

export function useAdminFeatureAvailability() {
  const { userInfo } = useUser();

  return {
    hasAdminAccess: Boolean(userInfo?.client_admin),
  };
}

export function useAdminMenuItems() {
  return ADMIN_MENU_DEFINITIONS;
}

export function getAdminLandingMenuItems() {
  return ADMIN_MENU_DEFINITIONS.filter(isRoutableAdminMenuItem);
}

export function getAdminMenuDefinitions() {
  return ADMIN_MENU_DEFINITIONS;
}
